if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          paginationButtons: Array.from(this.querySelectorAll('[data-media-gallery-pagination-button]')),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.controlsIdleDelay = 1800;
        this.controlsIdleTimeout = null;
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        this.elements.paginationButtons.forEach((button) => {
          button.addEventListener('click', this.setActiveMedia.bind(this, button.dataset.target, false));
        });
        this.bindControlsIdleState();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        window.requestAnimationFrame(() => {
          this.setActivePagination(this.elements.viewer.currentPage || this.getActiveMediaPosition());
          this.activateControls();
        });
      }

      onSlideChanged(event) {
        const currentMediaId = event.detail.currentElement.dataset.mediaId;
        this.activateControls();
        this.setActivePagination(event.detail.currentPage);
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, shouldScroll = true) {
        this.activateControls();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!shouldScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        const mediaPosition = this.getMediaPosition(activeMedia);
        this.setActivePagination(mediaPosition);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            return;
          }
        }

        this.announceLiveRegion(activeMedia, mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      setActivePagination(position) {
        if (!this.elements.paginationButtons.length || !position) return;

        this.elements.paginationButtons.forEach((button, index) => {
          const isActive = index === position - 1;
          button.classList.toggle('slider-counter__link--active', isActive);

          if (isActive) {
            button.setAttribute('aria-current', true);
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      getMediaPosition(mediaItem) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        return mediaItems.findIndex((element) => element.dataset.mediaId === mediaItem.dataset.mediaId) + 1;
      }

      getActiveMediaPosition() {
        const activeMedia =
          this.elements.viewer.querySelector('.is-active') || this.elements.viewer.querySelector('[data-media-id]');
        return activeMedia ? this.getMediaPosition(activeMedia) : 0;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindControlsIdleState() {
        ['pointerdown', 'touchstart', 'focusin'].forEach((eventName) => {
          this.addEventListener(eventName, () => this.activateControls(), { passive: true });
        });

        this.elements.viewer.addEventListener(
          'scroll',
          () => this.activateControls(),
          { passive: true }
        );
      }

      activateControls() {
        if (!this.elements.paginationButtons.length || this.mql.matches) return;

        this.classList.remove('media-gallery--controls-idle');

        window.clearTimeout(this.controlsIdleTimeout);
        this.controlsIdleTimeout = window.setTimeout(() => {
          this.classList.add('media-gallery--controls-idle');
        }, this.controlsIdleDelay);
      }
    }
  );
}
